import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import '../design/design-guidelines-styles.css';
import { Paragraph } from 'react-magma-dom';
export const _frontmatter = {
  "pageTitle": "Typography Design Guidelines",
  "title": "Typography",
  "order": 5
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const LeadParagraph = makeShortcode("LeadParagraph");
const Link = makeShortcode("Link");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <PageContent componentName="typography" type="design_intro" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  Cengage applications need clear and consistent headings, highly legible body
  paragraphs, and clear labels. Our typefaces are chosen for legibility and
  maximum support for mathematical characters.
      </LeadParagraph>
      <Paragraph mdxType="Paragraph">
  By consistently tying typographic styles to appropriate functions in the
  interface, we create a clear visual pattern for students and faculty to follow
  while they’re interacting with our products.
      </Paragraph>
      <hr></hr>
      <h2>{`Magma Type Sets`}</h2>
      <p>{`Magma has three sets of ready-made typographic elements that you can choose from and should be adhered to when at all possible. Each one combines a font family with calibrated sizes and weights to work within applications or editorial content. The three sets are called `}<strong parentName="p">{`Productive`}</strong>{`, `}<strong parentName="p">{`Expressive`}</strong>{`, and `}<strong parentName="p">{`Narrative`}</strong>{`.`}</p>
      <p><em parentName="p"><strong parentName="em">{`IMPORTANT:`}</strong>{` The Productive set is used by default in the components so you won't actually see it referred to in the code. You can easily change to Expressive or Narrative when appropriate by adding the necessary prop.`}</em></p>
      <h3>{`Productive Type Set (Default)`}</h3>
      <p>{`Productive type is reserved for use in web-based product design, where the user needs to focus on tasks. The Productive styles are curated to create a series of clear user expectations about hierarchy. The difference between the Productive and Expressive styles is mainly evident in the Headings.`}</p>
      <h4>{`Productive Body Text Styles`}</h4>
      <div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="bodyLarge" mdxType="Paragraph">
        This is a good size for higher emphasis text, as well as the subheadline
        of a page.
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="bodyMedium" mdxType="Paragraph">
        This is commonly used in layouts for paragraphs. The line height and
        size make for comfortable, long-form reading, in mediums that allow for
        more space. This size type is the basis for how all other text is
        calculated.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / Medium</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 16px / 1em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="bodySmall" mdxType="Paragraph">
        This is commonly used for helper and error text on inputs, as well as
        other explanatory text.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / Small</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 14px / .875em</p>
      <p>Line-height: 20px / 1.25em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: .16px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="bodyXSmall" mdxType="Paragraph">
        This is good to use for captions and other meta-data or explanatory
        text.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / X-Small</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 12px / .75em</p>
      <p>Line-height: 16px / 1em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: .32px</p>
    </div>
  </div>
      </div>
      <span className="horizontal-spacer" />
      <h4>{`Productive Heading Text Styles`}</h4>
      <div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="headingXLarge" mdxType="Paragraph">
        This is used for layout headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 36px / 2.25em</p>
      <p>Line-height: 48px / 3em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="headingLarge" mdxType="Paragraph">
        This is used for layout headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="headingMedium" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="headingSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="headingXSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Heading / X-Small</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="heading2XSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Heading / 2X-Small</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 12px / .75em</p>
      <p>Line-height: 16px / 1em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: .32px</p>
    </div>
  </div>
      </div>
      <span className="horizontal-spacer" />
      <h3>{`Expressive Type Set`}</h3>
      <p>{`Expressive type is reserved for use in editorial and digital marketing experiences which may require more dynamic typographic hierarchies and more variety in the Heading scale. The Expressive styles are curated to create a series of clear user expectations while still allowing for a more dramatic, graphic use of type.`}</p>
      <h4>{`Expressive Body Text Styles`}</h4>
      <div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="bodyLarge" mdxType="Paragraph">
        This is a good size for higher emphasis text, as well as the subheadline
        of a page.
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 20px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
      <p>Color: indigo600 / #292F7C</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
      <p>Color: indigo600 / #292F7C</p>
    </div>
  </div>
      </div>
      <span className="horizontal-spacer" />
      <h4>{`Expressive Heading Text Styles`}</h4>
      <div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="heading2XLarge" mdxType="Paragraph">
        This is used for layout headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / 2X-Large</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 64px / 4em</p>
      <p>Line-height: 84px / 5.25em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / 2X-Large</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 48px / 3em</p>
      <p>Line-height: 64px / 4em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="headingXLarge" mdxType="Paragraph">
        This is used for layout headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 48px / 3em</p>
      <p>Line-height: 64px / 4em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 36px / 2.25em</p>
      <p>Line-height: 48px / 3em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="headingLarge" mdxType="Paragraph">
        This is used for layout headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 36px / 2.25em</p>
      <p>Line-height: 48px / 3em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="headingMedium" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="headingSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="headingXSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / X-Small</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / X-Small</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 18px / 1.25em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="heading2XSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / 2X-Small</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: .32px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / 2X-Small</strong>
      </p>
      <p>Typeface: Inter</p>
      <p>Size: 16px / 1em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: .32px</p>
    </div>
  </div>
      </div>
      <span className="horizontal-spacer" />
      <h3>{`Narrative Type Set`}</h3>
      <p>{`Narrative type uses Noto Serif but is otherwise styled the same as the default Productive variant. The Narrative styles are curated to create a series of clear user expectations about hierarchy, but in a font that is more pleasurable for reading long passages of text.`}</p>
      <h4>{`Narrative Body Text Styles`}</h4>
      <div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="bodyLarge" mdxType="Paragraph">
        This is a good size for higher emphasis text, as well as the subheadline
        of a page.
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="bodyMedium" mdxType="Paragraph">
        This is commonly used in layouts for paragraphs. The line height and
        size make for comfortable, long-form reading, in mediums that allow for
        more space. This size type is the basis for how all other text is
        calculated.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / Medium</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 16px / 1em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="bodySmall" mdxType="Paragraph">
        This size is great for creating contrast between the main paragraph of
        the passage and secondary information.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 14px / .875em</p>
      <p>Line-height: 20px / 1.25em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: .16px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="bodyXSmall" mdxType="Paragraph">
        This is good to use for captions and other meta-data or explanatory
        text.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / X-Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 12px / .75em</p>
      <p>Line-height: 16px / 1em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: .32px</p>
    </div>
  </div>
      </div>
      <span className="horizontal-spacer" />
      <h4>{`Narrative Heading Text Styles`}</h4>
      <div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="headingXLarge" mdxType="Paragraph">
        This is commonly used for H1 headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 36px / 2.25em</p>
      <p>Line-height: 48px / 3em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="headingLarge" mdxType="Paragraph">
        This is commonly used for H2 headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="headingMedium" mdxType="Paragraph">
        This is commonly used for H3 headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="headingSmall" mdxType="Paragraph">
        This is commonly used for H4 headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="headingXSmall" mdxType="Paragraph">
        This is commonly used for H5 headings
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Heading / X-Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="heading2XSmall" mdxType="Paragraph">
        This is commonly used for H6 headings
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Heading / 2X-Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 12px / .75em</p>
      <p>Line-height: 16px / 1em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: .32px</p>
    </div>
  </div>
      </div>
      <span className="horizontal-spacer" />
      <h3>{`Changing Headings Styles`}</h3>
      <p>{`The headings presets above are assigned by default to H1 through H6 in the `}<Link to="/api/heading/" mdxType="Link">{`Headings component`}</Link>{`. But it's relatively common to need a specific heading type in the markup, but not want the default styling. For this reason we have made it very easy to change which styling preset you use in the headings component.`}</p>
      <p>{`One example of this is in the `}<Link to="/api/modal/" mdxType="Link">{`Modal component`}</Link>{`. For optimal accessibility the title of the modal needs to be an H1, but we definitely don't want it to be styled with text that large. So we use an H1, but apply the Productive Heading Small style to it. This flexibility allows consumers of Magma to design using the styles that work best for the UI, but still have a clear informational hierarchy for assistive technologies.`}</p>
      <figure>{`
  `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1035px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "43.62934362934363%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAJCAYAAAAywQxIAAAACXBIWXMAAAsTAAALEwEAmpwYAAABlUlEQVQoz6WPvUoDQRSF9w0sbEUsLLVXBH0AkRRCFNkF0d0Nlor6CMHGRmxExNZSSPIWCdGINm4mbhRX8ifubtb9m3tkxvjbWHjgY5i59545VzENnedyuS9Mkxu6LtHXdW78Qr6JumF8Ypri1LmuG1zJHx6DWbe4rNVwVavh+uYGFmOo3zXA7m3UB7BviJplWQNuwZiN8/MCNE2FsndyRpTE1Gq1qd3pUPfxiRAkhCCi1OkRf3r+ifNMcAMC8IOLyytSVZWUg6NTCHU6XXi+Dx7FSKIYD6v7sIdX0BxbR3NkFc3RNdyPm7CHlmDPbCMCwJMUcRzL+Wq1Ck3ToBSLRXDO4fs++v0+wtdXiJbm7jHqExtozO6ATW+BTW2iMbcLNrmBxnIeASWIwwhhGErDSqUCVVXfDYVc10UURUjTVBJ4PtxWF15b0HtH3Ftd9F9c2ZMkyWdCYZjNZqGUSiVp5DgOPM+TRZH4LxGRRBgLlctlZDKZr4RiZRH/49c/GfSJMB+Gy9klKIVCAf+RSClXLlcwv7iAN2S4Z1ynrqtFAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": " ",
            "title": " ",
            "src": "/version/6.0.0/static/549e2c69eed42df74faddd4975015c44/e3189/modal-title-style.png",
            "srcSet": ["/version/6.0.0/static/549e2c69eed42df74faddd4975015c44/a2ead/modal-title-style.png 259w", "/version/6.0.0/static/549e2c69eed42df74faddd4975015c44/6b9fd/modal-title-style.png 518w", "/version/6.0.0/static/549e2c69eed42df74faddd4975015c44/e3189/modal-title-style.png 1035w", "/version/6.0.0/static/549e2c69eed42df74faddd4975015c44/4352a/modal-title-style.png 1364w"],
            "sizes": "(max-width: 1035px) 100vw, 1035px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </figure>
      <h4>{`Consistency is Key`}</h4>
      <p>{`Continuing from above, there isn't just one way an H1 or and H3 should be styled. We have chosen logical default styles, but it's easy to say you need an H3 to be styled using one of the larger or smaller presets. The key is to be consistent. If you style a page title using productive-heading-large, then you need to use the same styling on all of the pages. If you use "productive-heading-xsmall" to style the title of a section, then you should use the same styling on equivalent sections.`}</p>
      <h3>{`Responsive Behavior`}</h3>
      <p>{`Another advantage to using the presets above is that Responsive behavior is already built into each style so you don't have to figure out and include it yourself. This means a number of the larger sizes will automatically reduce in size at 600px wide. This ensures we have appropriate text sizes on smaller devices. The breakpoint can be overridden if you want this change to happen earlier or later.`}</p>
      <figure>{`
  `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1035px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "47.87644787644788%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKCAIAAAA7N+mxAAAACXBIWXMAAAsTAAALEwEAmpwYAAABN0lEQVQoz43Ru27CMBQG4LwuDEhEZeepTGEnQ4IQKFK42Bw7jkw8YEEl4mAH4qoJRTC04ps8nF8+F68sS914PN7nGWPqhrX2drtVT+y/qqrytNZJkmCMGWOEEOdcXdfuPZ61Vil1OByKojgej6ZhrdVaT6dThNBnAyE0Go3G4/FkMkEIxXF8/5k3GGNpmjLGKKVCiCAIfN8fDAYfjU6n0+12fd/v9/u9Xm84HK7Xa68oCvoKADjnURRtNhspZZ7nQoiyLI0xl8tFa+2cWywWs9nsz/B8Pk/TlHOulJJSWmvbdVyvV+fcarW6h8krjDGlNIoiAJBScs6zLGO/ACDP8+VyGYbhz6m+Xp1Op/P5TAjZbrdtNQA8+trtdvv9Po5jAPDaNp61p5JSYow558/JdighRJIkSqlvSbUV1K9PKakAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": " ",
            "title": " ",
            "src": "/version/6.0.0/static/a423b000687c7ea4fb1c39f230b88821/e3189/responsive-example.png",
            "srcSet": ["/version/6.0.0/static/a423b000687c7ea4fb1c39f230b88821/a2ead/responsive-example.png 259w", "/version/6.0.0/static/a423b000687c7ea4fb1c39f230b88821/6b9fd/responsive-example.png 518w", "/version/6.0.0/static/a423b000687c7ea4fb1c39f230b88821/e3189/responsive-example.png 1035w", "/version/6.0.0/static/a423b000687c7ea4fb1c39f230b88821/f793b/responsive-example.png 1404w"],
            "sizes": "(max-width: 1035px) 100vw, 1035px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </figure>
      <hr></hr>
      <h2>{`Typefaces`}</h2>
      <h3>{`Inter`}</h3>
      <p>{`Our primary typeface is Inter. It’s clear and approachable and works well for headlines, subheads, navigation, and body copy.`}</p>
      <a href="https://fonts.google.com/specimen/Inter" target="_blank" rel="noreferrer">
  Download Inter
      </a>
      <p>{`Inter was designed for computer screens. Its tall x-height and carefully drawn
punctuation support legibility across interface sizes, while its broad range of
weights makes it suitable for both text and display use.`}</p>
      <h3>{`Noto Serif`}</h3>
      <p>{`Noto Serif is a beautiful serif font. It's easy to read, provides a nice contrast with Inter and has great support for special characters. At this point, Noto Serif should primarily be used for the reader applications that display our text books.`}</p>
      <undefined><a {...{
          "href": "https://fonts.google.com/specimen/Noto+Serif",
          "target": "_blank",
          "rel": "noreferrer"
        }}>{`
  Download Noto Serif
`}</a>
        <div {...{
          "className": "two-col"
        }}>{`
  `}<div parentName="div" {...{
            "className": "row"
          }}>{`
    `}<div parentName="div" {...{
              "className": "col col-1"
            }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                  "className": "gatsby-resp-image-wrapper",
                  "style": {
                    "position": "relative",
                    "display": "block",
                    "marginLeft": "auto",
                    "marginRight": "auto",
                    "maxWidth": "756px"
                  }
                }}>{`
      `}<span parentName="span" {...{
                    "className": "gatsby-resp-image-background-image",
                    "style": {
                      "paddingBottom": "66.02316602316603%",
                      "position": "relative",
                      "bottom": "0",
                      "left": "0",
                      "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABmElEQVQoz32STavqMBBA/bv6L4of4Kpu3bjSgiCKbrQKomvduRAxPNvGJNAWQUmbpGmdy23e8z646FkMIcnJJDOpiBKlVBRF9/tdSimESJIkTVMTxXsqZisAWJY1GAwAgHNeFIUQQmutlPrgV9I0zfMcY2zbtmVZQoiiKFarVa/XcxwnjuMPfsWknU6nh8Oh0Wjs93sA2G63lFLbtl3XBYAkSd5eWynV6XTm83m9Xu92uwAwmUzW63Wz2Vwul59kANhsNo7jAMDxeKxWqwihWq0Wx3Gr1RqPx59kpdRwOByNRlrr0+nUbrd3u91isej3+7PZzHVdrfXbN6dpaqqalBRFIaXM81wp9Xw+pZRmnnP+u3/fsud5l8vF8zyMMUIoCAKE0J8Ss3Q+nwkhGONLyfV6NS34rjYhhDFGKfV9n1IaBMFrQAihJYwxQsjj8RBC/GTmnAdBYLYihHzfxxhHJYyxMAxvt1tcEoYh59x8wR+ZUhpFkTmCMRbHcZ7nWZZprbMsU0pl/zDmy//bZxMNUsr0P155ftf8CzlpwZdKRN7yAAAAAElFTkSuQmCC')",
                      "backgroundSize": "cover",
                      "display": "block"
                    }
                  }}></span>{`
  `}<img parentName="span" {...{
                    "className": "gatsby-resp-image-image",
                    "alt": " ",
                    "title": " ",
                    "src": "/version/6.0.0/static/f2a309f68dfda25e4170e7c5bb7adbd9/8ae3e/noto-serif-characters.png",
                    "srcSet": ["/version/6.0.0/static/f2a309f68dfda25e4170e7c5bb7adbd9/a2ead/noto-serif-characters.png 259w", "/version/6.0.0/static/f2a309f68dfda25e4170e7c5bb7adbd9/6b9fd/noto-serif-characters.png 518w", "/version/6.0.0/static/f2a309f68dfda25e4170e7c5bb7adbd9/8ae3e/noto-serif-characters.png 756w"],
                    "sizes": "(max-width: 756px) 100vw, 756px",
                    "style": {
                      "width": "100%",
                      "height": "100%",
                      "margin": "0",
                      "verticalAlign": "middle",
                      "position": "absolute",
                      "top": "0",
                      "left": "0"
                    },
                    "loading": "lazy"
                  }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
              "className": "col col-2"
            }}>{`
      `}<p parentName="div">{`
        When text is rendered by a computer, sometimes there will be characters
        in the text that can not be displayed, because no font that supports
        them is available to the computer. When this occurs, small boxes are
        shown to represent the characters. We call those small boxes “tofu,” and
        we want to remove tofu from the Web. This is how the Noto font families
        got their name.
      `}</p>{`
      `}<p parentName="div">{`
        Noto helps to make the web more beautiful across platforms for all
        languages. Currently, Noto covers over 30 scripts, and will cover all of
        Unicode in the future. This is the Serif Latin, Greek and Cyrillic
        family. It has Regular, Bold, Italic and Bold Italic styles and is
        hinted.
      `}</p>{`
    `}</div>{`
  `}</div>
        </div></undefined>
      <hr></hr>
      <h2>{`Type Scale`}</h2>
      <p>{`The font sizes used in the sets above come from the full type scale. This type scale is based on the Major Second Type Scale (1.125) but is slightly modified to round to whole numbers and create a simple pattern in the difference between each size. Small font sizes differ by 2px, and then that difference increases by 4px and then 6px as the size gets larger.`}</p>
      <p>{`Line-heights are also defined at this level to adhere to an 8px spacing system, and should not be changed unless absolutely necessary`}</p>
      <p>{`If you have a need to use a font size outside of the pre-made sets above, you may only choose from this list.`}</p>
      <figure>{`
  `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1035px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "122.00772200772201%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAYCAIAAAB1KUohAAAACXBIWXMAAAsTAAALEwEAmpwYAAACDUlEQVQ4y6WUWbOiMBCF+f//zCofFLVcUNFSkSBLQvZA6Klrz6ByHWem5jxQYfno7pMDAfyjul8CgKDrujzPvfeUUufc8+2BAKBpmjRNL3d9wQCglPLeG2OEEMaY7qOaphFC/KyMcHOXUsoY87lt771SCtdfsHOu6zpjDGOMc940zeeZXyrj00opSmld11LKz7CU8gEbY3Bmfhda8AHWWr/AAKC1ppRi59bav62MbUspq6qq6xqPvyuOlR+GPcOMMYRxz/8MY9ucc0JIWZaEkKqqsizD3r7DSqlHwrBDIURRFJzzfvK3tuOmvoEJIXVd3263siyLomCM4dMDuLczQJ8BgFJ6Pp+zu06nU1VVp9OpLMsB/9J2b5i1ljEmpeScV1Wltcaof4d7LwNs2Ht/u92iKNrv93me7/f7KIryPI/jOE3TZ957/xJPlLW2rmuttbWWUoohd84JIQbb1r8owK8MANI0DcNws9kcj8ckScIwXK1Wl8tlu93O5/Oe8d5zzoeV27a11jrncCGlbNvWOYexfxuYALMFANfrdTwe73a72WxGCAnDcDKZrNfrJEkWi8VyucQdHc6MK621lFIIgR8GpTTLMimlUgqvv5kZfwYAwBiL4xhDQinN8zzLMkppURRa6ziOD4cDFn8JCZ4QQiaTyXQ6Xa/Xh8Pher2ORiO8gv4tFguEewsC+A/9ABxEcvXCJt3sAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": " ",
            "title": " ",
            "src": "/version/6.0.0/static/872cc46d87d31a873b9a05c997e03677/e3189/type-scale.png",
            "srcSet": ["/version/6.0.0/static/872cc46d87d31a873b9a05c997e03677/a2ead/type-scale.png 259w", "/version/6.0.0/static/872cc46d87d31a873b9a05c997e03677/6b9fd/type-scale.png 518w", "/version/6.0.0/static/872cc46d87d31a873b9a05c997e03677/e3189/type-scale.png 1035w", "/version/6.0.0/static/872cc46d87d31a873b9a05c997e03677/f793b/type-scale.png 1404w"],
            "sizes": "(max-width: 1035px) 100vw, 1035px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </figure>
      <hr></hr>
      <h2>{`Styles`}</h2>
      <h3>{`Weight`}</h3>
      <p>{`Font weight is an important typographic variable that can add emphasis and differentiate content hierarchy. Font weight and size pairings must be carefully balanced. A bold weight will always have more emphasis than a lighter weight font of the same size. However, a lighter weight font can rank hierarchically higher than a bold font if the lighter weight type size is significantly larger than the bold one. The weights available in Magma are `}<inlineCode parentName="p">{`thin (100)`}</inlineCode>{`, `}<inlineCode parentName="p">{`extra-light (200)`}</inlineCode>{`, `}<inlineCode parentName="p">{`light (300)`}</inlineCode>{`, `}<inlineCode parentName="p">{`regular (400)`}</inlineCode>{`, `}<inlineCode parentName="p">{`medium (500)`}</inlineCode>{`, `}<inlineCode parentName="p">{`semi-bold (600)`}</inlineCode>{`, `}<inlineCode parentName="p">{`bold (700)`}</inlineCode>{`, `}<inlineCode parentName="p">{`extra-bold (800)`}</inlineCode>{` and `}<inlineCode parentName="p">{`black (900)`}</inlineCode>{`.`}</p>
      <div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "118.91891891891892%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAYCAIAAAB1KUohAAAACXBIWXMAAAsTAAALEwEAmpwYAAACSUlEQVQ4y6WSzW+iQBiH+/+fPezNgzGVA6JWrdbPxJOxCKIoFSOKgPLhBGGGghLdZCbdtVa6u9nnNEzy8Hvnfd+H9//g4foj/MI/yK7rHg4H3/cPhwM5Qwj/Viaa53lEdl0XIfRNIbcyAWFc1/U8z/d98jvf9xPlIAi2262u61uMYRiqqlqW5TiOjrFt+7tk8kISCwAgab7vBx8kymEYqqo6w2iapijKdDqdzWa2bUdR9NW8TfY8z7ZthBDEeBiE0PF4vDu2T8k8zw+Hw/F4vFgsptOpruvz+XwwGAyHQ1EU/9BtAIAkSZPJRNf11Wq13+9N07Qsa7fbmab5tfhPyRzHybL89vYmy7Kqquv12jCMCWY8HgMAwjC89n/LURSxLNvv90nlgiAsFgtFUV5fX3meZ1kWAHA6ne7LQRAYhgEwqqoCACzL0jTNcRwNoyiK67rXL/8kd7tdmqabzebT01Oj0ajX651Oh+O4fD5frVYLhcJms7kOv20YWQYIoeM4pmnuMVEUkWkhhBLXM5fLPT4+5nK5drvdarWKxWImk6nVahBCsnaJ3SbJjuPYtk12IwgCchPH8QVzOp3uyGEYQggpiioWiwzDlMvlVCqVz+fT6XSv16Mo6gemUqkkli3L8nw+Xy6XkiRxHCdJEtkzURQFQRiNRoIgJI6qXq83m01RFHu9nqZp5OXPz88sy5Ky4zi+n4wQKpVK2WyWpmmGYQaDQalUYhiGpumXlxf0QWLDzufz5YMYc7lczufzTeB9GV1BBv7rcFf+CVf3K6/veNOgAAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": " ",
                  "title": " ",
                  "src": "/version/6.0.0/static/bec4223764b4b2866d68e2cf5fc417cc/8ae3e/font-weights.png",
                  "srcSet": ["/version/6.0.0/static/bec4223764b4b2866d68e2cf5fc417cc/a2ead/font-weights.png 259w", "/version/6.0.0/static/bec4223764b4b2866d68e2cf5fc417cc/6b9fd/font-weights.png 518w", "/version/6.0.0/static/bec4223764b4b2866d68e2cf5fc417cc/8ae3e/font-weights.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
  `}</div>
        </div></div>
      <h3>{`Italic`}</h3>
      <p>{`Each weight has an italic style, which should only be used when you need to emphasize certain words in a sentence (titles of works, technical terms, names of devices, captions, etc.).`}</p>
      <div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "118.91891891891892%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAYCAIAAAB1KUohAAAACXBIWXMAAAsTAAALEwEAmpwYAAADF0lEQVQ4y6WUW0/iQBiG+f9/QL0wxgQjwUgJEqhGvSBGKuFQLdBOCwJtaTulh5npgbbgutkOy+qessk+F+10knfed75vOoX1f1CgryTn44CO/0mMcoIgwDmEkDAMfd/HGAdB8DdxHMdBEFAxQsh1XarxfZ9O/inCThyGYZATRVEcx1QchiFN8SfzQpIkYRgahrFcLi3LghAahrFYLBzHsW3bNM3lcuk4zt+coyhar9dRFNEIcc5H5ziOf+9MCNE0bTgcSpJkmqaiKJIkTadTCKGiKLIsAwA8z0vT9Kclds4Y49VqRaeoIXWjQRBC6/U6TdOfKvfNGWPc6XQAAIqiiKIoCILrupIkjUYjnuchhKIodjqdfr8vimKapp+coyiCEE4mEwCAruvz+TwIAtu2VVXVdd3+jmmaEMJP4iRJgiAQBAHkSJK0WCyWy+V0OgUAjMdjy7JUVRUEYTweS5Lk+/5+87uC9Xo9juO63e5gMBAEQdd1AECv1xsMBqqqvr6+djqdl5cXnufDMPwhprENw8AYI4Q0TaMdtizLtm3P81RVhRBijOfzuaZps9mMEEIrV0jT1Pf9er1+e3t7d3dXrVbr9XqtVmu32w8PD81m8+rqis+pVCosy9ZqNQhhlmVxHO9ahRCiJxxjHOUQQjabjeM4EELP81zXzbKMBt53u5BlmWVZx8fHl5eXpVLp8fGxWCyen5+Xy2VRFO/v76vV6unpaaPRoEfgk5juebVaua5LzzAdeJ6HECKERFHkOI7rupvN5kvO29vbbs9ZlpmmeXZ2Vs1pNBoHBwcMwxSLxevr61KpdHFxcXR0xPN8s9k8PDw8OTkplUq2bWdZ9q1VCCFJkmRZns1msizzPA8AEEVR1/XJZDIajURRNAxDURRBEIbDYb/fxxgnSbKrdq1Wa7Vaz8/PHMfJstxqtZ6enliW1TSt3W6zLHtzc8Nx3Ha7fX9//xQbQlgsFsvl8lVOt9st5zAM0+/3WZZlGKZSqdTrdULI/v/dFSxJkvcP0OUpm81m/7ndbn9/AdLe0lXpcz+z//z1PvgKLBUUGIEfbFYAAAAASUVORK5CYII=')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": " ",
                  "title": " ",
                  "src": "/version/6.0.0/static/5d49086f312daf75b93249cbc352db2e/8ae3e/font-italic.png",
                  "srcSet": ["/version/6.0.0/static/5d49086f312daf75b93249cbc352db2e/a2ead/font-italic.png 259w", "/version/6.0.0/static/5d49086f312daf75b93249cbc352db2e/6b9fd/font-italic.png 518w", "/version/6.0.0/static/5d49086f312daf75b93249cbc352db2e/8ae3e/font-italic.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
  `}</div>
        </div></div>
      <h3>{`Color`}</h3>
      <p>{`Type color should be carefully considered, with legibility and accessibility as your main concerns. Color can be helpful for reinforcing the meaning or context of a piece of text or action, but color should never be the only way this is communicated, or just for decoration. When text is generally kept neutral in color, then it can be much more powerful when you do need to use it to point out actions or a special important messages. The text itself is the clearest way to explain what the user needs to know, but icons can also help provide helpful clues.`}</p>
      <div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA50lEQVQoz62S22qEMBCG9/3fTvAm5mRCojknxo2i4La0UteWQmnrfzWnj5lh5vbyD90ugB/vOoxv3XP8CZ/9X3eepokQQin13kMItdaUUsaYlJJSGkIwxuwRIQRjbF3XT533ipxzCMF7r7WOMaaU+r7POccYrbXOuZSS937btidcSmnbFiFkrQ0hQAgJIQihruswxlJKhJDW2hhTSjmv+QYvyxJjHIYh5zyOY/rQPM8xRufcnvLe3+/3rztrrauqghAqpYQQdV0DADjnSimMMUKoaRrOOYQQAHDMfNGdD/1456s/7G/wK41e88hSrTnEAAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": " ",
                  "title": " ",
                  "src": "/version/6.0.0/static/22ef0cab587b26ed7b82e42453918d3d/8ae3e/text-color-neutral-1.png",
                  "srcSet": ["/version/6.0.0/static/22ef0cab587b26ed7b82e42453918d3d/a2ead/text-color-neutral-1.png 259w", "/version/6.0.0/static/22ef0cab587b26ed7b82e42453918d3d/6b9fd/text-color-neutral-1.png 518w", "/version/6.0.0/static/22ef0cab587b26ed7b82e42453918d3d/8ae3e/text-color-neutral-1.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            Text should generally be kept neutral in color until there's a
            reason to use a color.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABcUlEQVQoz7VS226CQBT0W5oAKgj2YlEEkRVRFBW81SZNk/5FBUlsLTdRq4AmTX+2UbyVhz40bTI5mXOyk9ndObEEMfo1Yn8gTqa3OJKzVj+0O0Js8U2cTOtISoWSQwTXYEyFMRVCh3BKQ1Lajuwn4RDG1JMYSWnXuddmx2+0vULJaffXoDprKF5NXgn1haSsaM4qClNJ8eryqiq91+UlkRnHce3oPKKBxQkuSRs0sBlgg4qbL1oUa/LiPMsY+aLFlqcs71CsyQAbvdTj+MH5hppIykruBQXeyXOW0g8aba/V9cvivNn1BWkh9wJQcTnBJTLji8Tz6dpxXCMyY4q1cqxJMm8kbeRYM8RV9oUqWgXeyRZMkjEYzr7NT0LPvRhCh6DiPjx9Kv2gJM4EaXH/+NEdbMTmElTdVseXe0H7bl1rLZV+0Bls0Es9GhWy+8awwpi2I2FVIVQNIwgP/LQkkZwPE/0YeFQcx0fnL4m0/7WeXxXR6oKnfcFGAAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": " ",
                  "title": " ",
                  "src": "/version/6.0.0/static/0c46197cad0f329289b9e1d1d97bb507/8ae3e/text-color-neutral-2.png",
                  "srcSet": ["/version/6.0.0/static/0c46197cad0f329289b9e1d1d97bb507/a2ead/text-color-neutral-2.png 259w", "/version/6.0.0/static/0c46197cad0f329289b9e1d1d97bb507/6b9fd/text-color-neutral-2.png 518w", "/version/6.0.0/static/0c46197cad0f329289b9e1d1d97bb507/8ae3e/text-color-neutral-2.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`On dark backgrounds, neutral text appears as white.`}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA7klEQVQoz7WSW2/CMAyF+f//bdLYjUE3BqNbk9a5uDGO20KYuk6I7mEPDKzzcBTp05F9Mjn8YyaXgFOvlNLRHwY7vFwrWWpxK2teDL57UoFUwBxxg37lTAZpn/6CO+moItKBDQsKG2bD0UWpBXNM+3SqXbsbwdGxvivs0mKOxW0Bz5V7c4I9DPMK5mAyCCroB10+lp83Hw01w2m+k2MXfWTL4qUJjdQi2KsJ0m5bKok0RctsmXRouf29s8kAN1jNSru0sIA+5EmraRGKoO9VNSthAfbV+LVn2P40csa1T8sbw2lsjuWnUfmX/WFnwV9cTfrCjejs6wAAAABJRU5ErkJggg==')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": " ",
                  "title": " ",
                  "src": "/version/6.0.0/static/0b9be4f47292b467fb92cb118ecffeeb/8ae3e/text-color-decoration.png",
                  "srcSet": ["/version/6.0.0/static/0b9be4f47292b467fb92cb118ecffeeb/a2ead/text-color-decoration.png 259w", "/version/6.0.0/static/0b9be4f47292b467fb92cb118ecffeeb/6b9fd/text-color-decoration.png 518w", "/version/6.0.0/static/0b9be4f47292b467fb92cb118ecffeeb/8ae3e/text-color-decoration.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-incorrect"
                }}>{`Incorrect`}</p>{`
          `}<p parentName="figcaption">{`Don't use color as decoration.`}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA00lEQVQoz72S3UrDUBCE+/6vJQqlllIFa0VETdNWTU6SY7Pnx505EqEYSG+0pcuy7M3wzbA7SkfU6CRiDjol7pffyZOSGYwWlzBTfD5SMjQL7J4ouZZjyArNHewSu2fUN7TLAVkd2ldKRskZSkhG/87YUNZ0m67bjLGirBiKvvMfcjSwC9RzVDNKDnsP+4BqpuVEzbWWYzUTSt4L3yfDMxi4dcd3W/o3xjohUH1CTNSkLsEfzoz2RT8uUN9qcdVZMFNU84SvY+7MP4kHdz7Ph/1H/A0rSv6BEjrq4QAAAABJRU5ErkJggg==')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": " ",
                  "title": " ",
                  "src": "/version/6.0.0/static/659f8e2fdef998b1004871720de6265e/8ae3e/text-color-contrast.png",
                  "srcSet": ["/version/6.0.0/static/659f8e2fdef998b1004871720de6265e/a2ead/text-color-contrast.png 259w", "/version/6.0.0/static/659f8e2fdef998b1004871720de6265e/6b9fd/text-color-contrast.png 518w", "/version/6.0.0/static/659f8e2fdef998b1004871720de6265e/8ae3e/text-color-contrast.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-incorrect"
                }}>{`Incorrect`}</p>{`
          `}<p parentName="figcaption">{`
            The contrast between text and its background needs to be 4.5:1, or
            3:1 for large (18px) text.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA70lEQVQoz62Sz26DMAzG+95Tj91jrOfuXaadFtQhQv4HHAIJ7UigAqZOU8V1g0+W5cvPtvx5N6/Qbi08/Wj+a2wwOUHoab93Ug6MRa0DpSHDkbLIxVDZXqpA6AAwGBOl7EvocH5z/hemlL4cjxeAiHFUqsuyQEgU8oqSKEQgpMN5DxC1CvTevX17v9XNFmsj9HE4PBdGaIeFTXl1bjsLXnCb1tcSHAfPC0d0jYlB3Kbuq7qfeZ7usFTydHo1TaFdDp5zm7ahKT0jJqkuWjVYO1J4xu0ns2ftiO8W8Fqfx3GcHnY/8lLzopinjT7s3/A3wT/yQAy1rQYAAAAASUVORK5CYII=')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": " ",
                  "title": " ",
                  "src": "/version/6.0.0/static/fa75588b31848e18ed3ccf74478f8e6c/8ae3e/text-color-meaning.png",
                  "srcSet": ["/version/6.0.0/static/fa75588b31848e18ed3ccf74478f8e6c/a2ead/text-color-meaning.png 259w", "/version/6.0.0/static/fa75588b31848e18ed3ccf74478f8e6c/6b9fd/text-color-meaning.png 518w", "/version/6.0.0/static/fa75588b31848e18ed3ccf74478f8e6c/8ae3e/text-color-meaning.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            Appropriate color usage can help reinforce the meaning of text, but
            is not a replacement for clear text and other helpful visual aids
            like icons.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAtUlEQVQoz62RO46EQAxE5+KEnIOYA0FEYmy3P2qDoGPY1UxPsuECFVp6rrLr9XNDr7vweUlPOI/j2DSNiKSU3J2Z53lOKYnIuq51EhHLsphZzhkRSylfGAC6rqtTdwcAZlbVaZpUlZkRMSLcvW4fhmHbtodit22rqiJCRIi477u7E1GN7e71KAAgomp7nucbZua+73POqlqZUoqZAUBEiIiqmhl+pKp/4Ls9H8fx35KfeNhl+BewQ/ExZhAZ3QAAAABJRU5ErkJggg==')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": " ",
                  "title": " ",
                  "src": "/version/6.0.0/static/91ea4ab5aeee7b904fee167950078938/8ae3e/text-color-meaning-2.png",
                  "srcSet": ["/version/6.0.0/static/91ea4ab5aeee7b904fee167950078938/a2ead/text-color-meaning-2.png 259w", "/version/6.0.0/static/91ea4ab5aeee7b904fee167950078938/6b9fd/text-color-meaning-2.png 518w", "/version/6.0.0/static/91ea4ab5aeee7b904fee167950078938/8ae3e/text-color-meaning-2.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            A message is successful if you can remove the color from it, and the
            meaning is still clear.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>
      </div>
      <h4>{`Subdued`}</h4>
      <p>{`It is common to lighten certain pieces of text in order to create more contrast. For example, making a subtitle more subdued than the title, or instructional text like the helper text on form inputs. This can be easily done by applying the "subdued" color to a piece of text. On a light background, this makes the color #707070. The inverse equivalent is a semi-transparent white.`}</p>
      <div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABPUlEQVQoz62S30rDMBSHd+2VCEIFBz6F4IOIKHOoDyD4SD6AoDIHIrQX/cNKJ94MhKm0XYe6Jm1JZW1OkqWyFkRwmwz9COHHST4OnKRWlMiSKnzt3+szqRV/YCoDQBRF4/GYMUYIoZSmaZrnOec8SZLqaK6MEFJVVdM013W73a5lWbZtO47T7/d93zdN0zAMAJgtCyEYY1mWAUCVeQkAcM4ZY0KIuZ0ppQihMAwxxoSQOI4xxkkcI4zDCI9GI0ph6YHJj5z3BtM0kYUs10xZ/qCq8gBLLpZ7KsF4oj7EN53oQsfnd8mtE12aaedRLparnukbelH2g/VGUD8OlOZg7WCwsutun9Lpjd9k8ho+bTa9jUNv68SrH7lKw1vde945ywu5SK7gAO/afXCtD1vGsGUO21ZwpYd2b/KP3/MTIVjdO4/ThFcAAAAASUVORK5CYII=')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": " ",
                  "title": " ",
                  "src": "/version/6.0.0/static/733e31641bc2732d9bcbd19893f8090c/8ae3e/subdued-example-1.png",
                  "srcSet": ["/version/6.0.0/static/733e31641bc2732d9bcbd19893f8090c/a2ead/subdued-example-1.png 259w", "/version/6.0.0/static/733e31641bc2732d9bcbd19893f8090c/6b9fd/subdued-example-1.png 518w", "/version/6.0.0/static/733e31641bc2732d9bcbd19893f8090c/8ae3e/subdued-example-1.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABJ0lEQVQoz6WSsU7DMBCGo+6MrIiHYEVUQkgspWJgZUHMbFGlKkKMvAcDb8OCEGRoSUQTWVYSbAfbtX0+lKZDl4ZUfLqzvPz3350uQES/Ae5CgP+gETPG8jynlGZZZozZTSyEoJRWVUUI0Vo75+yK5XastY0YAKSUlNKiKMqyJISUZdnWgi1475VSzrmg/QkhOOdNci6EYIzVdd3hrJQCgHXbhBDG2PePkFK283jvoZP1zGCskdoKqV4/YWnQQhO+38I8IiA65/R76ry3iK7ntgExe3hKju7Ss2l6HKbDSTqcJCchny2autDVQGAQZ5fTxWCcHd7mBzdfe1fZ/vV8cE5f3lZi+MM5eXz+OA3j8X08iuKLqHlHEZv3cEZEiyjRq43Q2OvOfwEHIeEs41InMAAAAABJRU5ErkJggg==')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": " ",
                  "title": " ",
                  "src": "/version/6.0.0/static/9a1972237695d7ba77cdbf42fbe31ca7/8ae3e/subdued-example-2.png",
                  "srcSet": ["/version/6.0.0/static/9a1972237695d7ba77cdbf42fbe31ca7/a2ead/subdued-example-2.png 259w", "/version/6.0.0/static/9a1972237695d7ba77cdbf42fbe31ca7/6b9fd/subdued-example-2.png 518w", "/version/6.0.0/static/9a1972237695d7ba77cdbf42fbe31ca7/8ae3e/subdued-example-2.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
  `}</div>
      </div>
      <hr></hr>
      <h2>{`Margins`}</h2>
      <p>{`Headings and paragraphs have margins built into them by default. These margins provide the desired rhythm created by spacing when they are used together in a traditional narrative context. These margins can be easily modified using the spacing scale or completely removed when using headings or paragraphs in other contexts.`}</p>
      <figure>{`
  `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1035px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "101.15830115830116%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAIAAAAC64paAAAACXBIWXMAAAsTAAALEwEAmpwYAAACNElEQVQ4y5XSa1PaQBQGYP//7+hNpTOdVsVLq1QFFNAKJIQkgtxDTAK5bC6b3ezbquDojOD0zPNhv5w5ey4beIgYCCAIQCACZD4y7+HNfdAZ0vlrbsYoYxzAhhDifqqZE3ncr5uTlmOqc0sP3J4xbPrzO4iZYBa4/YLDWZKmbJE8bEvSeUGtlJrnBb12YajyQPpTOcpPlCaIK+YWXPsVzh7/iw1AoPELxU1cfEUph9I2ils4/Yziljj9Ik4+iMLHl/D7E3Om8VNlAP2OpkkNtXkz6uodRR50NNAoi4lIQtDoDSJ7roykX/blH4GyR5S8J++Qdh7DMwxO3zY8S4kTJ+ljshCscUbPv7GLXVr6npZ3+OWuqOZXQW2fezZ9mjaA8UC5VS57+nVHrXbVmjlWwGdgzioZp5zz5cCUn6hu4jqHqxxqOdS2RXVrhU3UcnRmkChZrMroKD2pOmrfDFrXkTUAsRFYawieiudViX45U/Z4ez9TD6AdirWgH1HvnkTxorLZbQ3lqqnXTb0+1W6EO4FnrOQbWRqzZc9gd+VE3qPKAVOP0vYhOsfrdE9S3w7jZLGqmX7pSMeOXAj0IswmjPo600YaulG8HJjrdP3Z7dxWaTgEvwcz18t4slyVEMSSfKPiTSrckxGqIO11QpXTgC5vW8RzLbTqkSMJoiHU33ObpSFbXpggthKYV6nXQqQj1N4R6SwJ4oQueo7cHnEU6nWQjJAM30FHNAmSp2T8f4jH+Jf8Fzd9UAVKeMhuAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": " ",
            "title": " ",
            "src": "/version/6.0.0/static/6ec30449e0bd83adaf0324c12b49592c/e3189/margins-illustration.png",
            "srcSet": ["/version/6.0.0/static/6ec30449e0bd83adaf0324c12b49592c/a2ead/margins-illustration.png 259w", "/version/6.0.0/static/6ec30449e0bd83adaf0324c12b49592c/6b9fd/margins-illustration.png 518w", "/version/6.0.0/static/6ec30449e0bd83adaf0324c12b49592c/e3189/margins-illustration.png 1035w", "/version/6.0.0/static/6ec30449e0bd83adaf0324c12b49592c/f793b/margins-illustration.png 1404w"],
            "sizes": "(max-width: 1035px) 100vw, 1035px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </figure>
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      